import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Incident, IncidentStyle } from "@shared/types";
import { Button } from "@/components/ui/button";
import { cn, formatDateTime } from "@/lib/utils";

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"] as const;

const DOT_BY_STYLE: Record<IncidentStyle, string> = {
  info: "bg-status-maintenance",
  warning: "bg-status-pending",
  danger: "bg-status-down",
  success: "bg-status-up",
};

/** danger > warning > info > success — pick the marker for a day with several events */
const SEVERITY: IncidentStyle[] = ["danger", "warning", "info", "success"];

function monthKey(date: Date): number {
  return date.getFullYear() * 12 + date.getMonth();
}

function monthLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

export function EventCalendar({ incidents }: { incidents: Incident[] }) {
  const now = new Date();
  const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  // History window matches the API payload (unresolved + last 90 days).
  const minMonth = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  const [month, setMonth] = useState(currentMonth);

  const byDay = useMemo(() => {
    const map = new Map<string, Incident[]>();
    for (const incident of incidents) {
      const d = new Date(incident.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      map.get(key)?.push(incident) ?? map.set(key, [incident]);
    }
    return map;
  }, [incidents]);

  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const leadingBlanks = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7; // Monday-first
  const isCurrentMonth = monthKey(month) === monthKey(currentMonth);

  const monthIncidents = incidents
    .filter((incident) => {
      const d = new Date(incident.createdAt);
      return d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth();
    })
    .sort((a, b) => b.createdAt - a.createdAt);

  function shift(delta: number) {
    setMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  }

  return (
    <section className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
      <header className="flex items-center justify-between border-b px-5 py-4 sm:px-6">
        <h2 className="font-semibold">Event calendar</h2>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={monthKey(month) <= monthKey(minMonth)}
            onClick={() => shift(-1)}
            aria-label="Previous month"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="min-w-24 text-center tabular-nums">{monthLabel(month)}</span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={isCurrentMonth}
            onClick={() => shift(1)}
            aria-label="Next month"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </header>

      <div className="px-5 py-4 sm:px-6">
        <div className="grid grid-cols-7 text-center text-xs text-muted-foreground">
          {WEEKDAYS.map((day, index) => (
            <span key={index} className="py-1.5">
              {day}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-7 text-center text-sm">
          {Array.from({ length: leadingBlanks }, (_, index) => (
            <span key={`blank-${index}`} />
          ))}
          {Array.from({ length: daysInMonth }, (_, index) => {
            const dayNum = index + 1;
            const key = `${month.getFullYear()}-${month.getMonth()}-${dayNum}`;
            const events = byDay.get(key);
            const style = events
              ? SEVERITY.find((severity) => events.some((event) => event.style === severity))
              : undefined;
            const isToday = isCurrentMonth && dayNum === now.getDate();
            return (
              <span
                key={dayNum}
                title={events ? events.map((event) => event.title).join("\n") : undefined}
                className={cn(
                  "mx-auto flex h-9 w-9 flex-col items-center justify-center gap-0.5 rounded-md tabular-nums",
                  isToday && "bg-muted font-semibold",
                  events && "cursor-default",
                )}
              >
                <span className={cn("leading-none", !events && "text-muted-foreground")}>{dayNum}</span>
                <span className={cn("size-1.5 rounded-full", style ? DOT_BY_STYLE[style] : "bg-transparent")} />
              </span>
            );
          })}
        </div>

        {monthIncidents.length > 0 ? (
          <ul className="mt-4 space-y-3 border-t pt-4">
            {monthIncidents.map((incident) => (
              <li key={incident.id} className="flex items-start gap-2.5 text-sm">
                <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", DOT_BY_STYLE[incident.style])} />
                <div className="min-w-0">
                  <p className="font-medium">{incident.title}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs leading-5 text-muted-foreground">{incident.content}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatDateTime(incident.createdAt)}
                    {incident.resolvedAt != null && <span className="text-status-up"> · Resolved</span>}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 border-t pt-4 text-center text-xs text-muted-foreground">No events this month</p>
        )}
      </div>
    </section>
  );
}
